import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
// import LogoImg from "../../assets/image/Tyre_Company_logo.png";
import LogoImg from "../../assets/image/bill_logo.png";
import ApiUrl from "../Environment/ApiUrl";

const Invoice_bill = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [responseData, setResponseData] = useState({});
  const [isPrinting, setIsPrinting] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Effect for fetching sales data
  useEffect(() => {
    fetchSalesData();
  }, [id]);

  // Effect for triggering print dialog and handling navigation
  useEffect(() => {
    if (isPrinting) {
      const printTimeout = setTimeout(() => {
        window.print();
        // Set a timeout to detect if print is canceled (onafterprint not triggered)
        const cancelTimeout = setTimeout(() => {
          if (isPrinting) {
            setIsPrinting(false);
            navigate("/sales");
          }
        }, 500);

        window.onafterprint = () => {
          clearTimeout(cancelTimeout);
          setIsPrinting(false);
          navigate(-1);
        };

        return () => {
          clearTimeout(cancelTimeout);
          window.onafterprint = null;
        };
      }, 500);

      return () => {
        clearTimeout(printTimeout);
        window.onafterprint = null;
      };
    }
  }, [navigate, isPrinting]);

  const fetchSalesData = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/sales_data.php/${id}`);
      const salesList = response.data?.data || [];

      // Find the invoice that matches the ID from the route
      const matchedInvoice = salesList.find((item) => item.id === id);

      if (matchedInvoice) {
        setResponseData(matchedInvoice);
        console.log("matchedInvoice", matchedInvoice);
      } else {
        console.warn("No matching invoice found for ID:", id);
        setResponseData({});
      }
    } catch (error) {
      console.log("Error fetching data:", error.message);
    }
  };

  // Calculate totals based on MRP (assuming MRP includes GST)
  const calculateTotals = useMemo(() => {
    const items = responseData.Sales_Items || [];
    let taxableValue = 0;
    let totalCgst = 0;
    let totalSgst = 0;

    items.forEach((item) => {
      const gstRate = parseFloat(item.sales_gst || 0) / 100;
      const mrpIncludingGst = parseFloat(item.Mrp || 0);
      const baseMrp = mrpIncludingGst / (1 + gstRate);
      const itemTaxableValue = item.sales_quantity * baseMrp;
      const itemCgst = (itemTaxableValue * gstRate) / 2;
      const itemSgst = itemCgst;

      taxableValue += itemTaxableValue;
      totalCgst += itemCgst;
      totalSgst += itemSgst;
    });

    const grandTotal = taxableValue + totalCgst + totalSgst;
    const roundedTotal = Math.round(grandTotal * 100) / 100;
    const roundOff = Number((roundedTotal - grandTotal).toFixed(2));

    return {
      taxableValue: taxableValue.toFixed(2),
      cgstAmount: totalCgst.toFixed(2),
      sgstAmount: totalSgst.toFixed(2),
      totalTax: (totalCgst + totalSgst).toFixed(2),
      grandTotal: grandTotal.toFixed(2),
      roundedTotal: roundedTotal.toFixed(2),
      roundOff: roundOff.toFixed(2),
    };
  }, [responseData.Sales_Items]);

  const {
    taxableValue,
    cgstAmount,
    sgstAmount,
    totalTax,
    roundedTotal,
    roundOff,
  } = calculateTotals;

  // Pagination logic
  const totalItems = responseData.Sales_Items?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems =
    responseData.Sales_Items?.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    ) || [];

  // Calculate dynamic empty rows based on item count
  const emptyRowsNeeded = Math.max(0, 6 - currentItems.length);

  // Number to words conversion for Indian Rupees
  const numberToWords = (num) => {
    if (num === 0) return "Zero Only";

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const scales = ["", "Thousand", "Lakh", "Crore"];

    const convertLessThanThousand = (n) => {
      if (n === 0) return "";
      if (n < 20) return ones[n];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${ones[n % 10]}`.trim();
      return `${ones[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(
        n % 100
      )}`.trim();
    };

    const convert = (n) => {
      if (n === 0) return "";
      let result = "";
      let scaleIndex = 0;

      while (n > 0) {
        if (scaleIndex === 0) {
          // Handle hundreds
          if (n % 1000 !== 0) {
            result = `${convertLessThanThousand(n % 1000)} ${
              scales[scaleIndex]
            } ${result}`.trim();
          }
          n = Math.floor(n / 1000);
          scaleIndex++;
        } else if (scaleIndex === 1) {
          // Handle thousands
          if (n % 100 !== 0) {
            result = `${convertLessThanThousand(n % 100)} ${
              scales[scaleIndex]
            } ${result}`.trim();
          }
          n = Math.floor(n / 100);
          scaleIndex++;
        } else {
          // Handle lakhs and crores
          if (n % 100 !== 0) {
            result = `${convertLessThanThousand(n % 100)} ${
              scales[scaleIndex]
            } ${result}`.trim();
          }
          n = Math.floor(n / 100);
          scaleIndex++;
        }
      }
      return result;
    };

    // Handle integer and decimal parts
    const [integerPart, decimalPart = "0"] = num.toFixed(2).split(".");
    let words = convert(parseInt(integerPart));
    if (parseInt(decimalPart) > 0) {
      words += ` and ${convert(parseInt(decimalPart))} Paise`;
    }
    return `INR ${words || "Zero"} Only`;
  };

  // Handle cancel action
  const handleCancel = () => {
    setIsPrinting(false);
    navigate("/sales");
  };

  if (!responseData.Sales_Items?.length) {
    return <div>No sales items found for this invoice.</div>;
  }

  return (
    <div
      className="a4-wrapper"
      style={{
        width: "200mm",
        minHeight: "292mm",
        margin: "0 auto",
        backgroundColor: "white",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
      }}
    >
      <div className="container-fluid">
        {/* Cancel Button */}
        <div className="text-end mb-0">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            style={{
              padding: "5px 10px",
              fontSize: "12px",
              display: isPrinting ? "block" : "none",
            }}
          >
            Cancel
          </button>
        </div>

        {/* Pagination Controls (only show if multiple pages) */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mb-2">
            <button
              className="btn btn-sm btn-outline-primary me-2"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            <span className="align-self-center">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-primary ms-2"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}

        {/* Header */}
        <div className="p-2">
          <div className="row">
            <div className="col-lg-5 col-md-5 col-6 d-flex justify-content-center align-items-start">
              <img
                src={LogoImg}
                alt="Company-Logo"
                style={{ width: "100px", height: "160px" }}
              />
            </div>
            <div className="col-lg-7 col-md-7 col-6 text-end">
              <h5 className="fw-bold mb-1">Sri Krishna Tyres</h5>
              <div className="fs-6 fw-medium">
                MRF TYRES AND SERVICE FRANCHISE
              </div>
              <div style={{ fontSize: "15px" }}>
                Omlur Main Road Tharamangalam, 9524030256/9942043943,
              </div>
              <div style={{ fontSize: "15px" }}>Pincode:636502</div>
              <div style={{ fontSize: "15px" }}>Ph: Mobile: 9524030256</div>
              <div style={{ fontSize: "15px" }}>GST No: 33BCOPM0715M1ZG</div>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="row mt-1">
          <div
            className="col-lg-7 col-md-7 col-7 p-1 border border-dark"
            style={{ height: "70px" }}
          >
            <div className="d-flex">
              <span style={{ width: "65px" }}>NAME</span>
              <span>:</span>
              <span className="ps-2 fw-semibold">
                {responseData.customer?.name}
              </span>
            </div>

            <div className="d-flex">
              <span style={{ width: "65px" }}>ADDRESS</span>
              <span>:</span>
              <span className="ps-2 fw-semibold">
                {responseData.customer?.address}
              </span>
            </div>


            
                <div className="d-flex">
              <span style={{ width: "65px" }}>MOBILE </span>
              <span>:</span>
              <span className="ps-2 fw-semibold">
                {responseData.customer?.mobile_number}
              </span>
            </div>
            
          </div>

          <div
            className="col-lg-4 col-md-4 col-4 p-1 border border-dark"
            style={{ marginLeft: "43px" }}
          >
            <div className="d-flex mt-3">
              <span style={{ width: "65px" }}>BILL NO</span>
              <span>:</span>
              <span className="ps-2 fw-semibold">{responseData.Sales_ID}</span>
            </div>
            <div className="d-flex">
              <span style={{ width: "65px" }}>DATE</span>
              <span>:</span>
              <span className="ps-2 fw-semibold">
                {responseData.Purchase_Date}
              </span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="row mt-3">
          <div className="col-12" style={{ marginLeft: "-10px" }}>
            <table className="table text-center align-middle">
              <thead>
                <tr>
                  <th
                    style={{ width: "40%" }}
                    className="border border-dark border-end"
                  >
                    PARTICULARS
                  </th>
                  <th
                    style={{ width: "11%" }}
                    className="border border-dark border-end"
                  >
                    HSN CODE
                  </th>
                  <th
                    style={{ width: "10%" }}
                    className="border border-dark border-end"
                  >
                    QTY
                  </th>
                  <th
                    style={{ width: "15%" }}
                    className="border border-dark border-end"
                  >
                    RATE / UNIT
                  </th>
                  <th
                    style={{ width: "10%" }}
                    className="border border-dark border-end"
                  >
                    GST %
                  </th>
                  <th
                    style={{ width: "15%" }}
                    className="border border-dark border-end"
                  >
                    AMOUNT
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* Data Row */}
                {currentItems.map((item, index) => {
                          const gstRate = parseFloat(item.sales_gst || 0) / 100;
                        const mrpIncludingGst = parseFloat(item.Mrp || 0);
                        const baseMrp1 = ( mrpIncludingGst *(1 + gstRate) ).toFixed(2);
                        const baseMrp2 = (  baseMrp1- mrpIncludingGst).toFixed(2);
                        const baseMrp = (  mrpIncludingGst- baseMrp2 ).toFixed(2);
                        const totalAmount = (item.sales_quantity * mrpIncludingGst).toFixed(2);

                  return (
                    <tr key={index}>
                      {/* Product details */}
                      <td className="text-start border border-top-0 border-bottom-0 border-dark border-end">
                        {item.category || "N/A"} - {item.sub_category || "N/A"}{" "}
                        - {item.tyre_size || "N/A"} - {item.type || "N/A"}
                      </td>

                      {/* HSN Code */}
                      <td className="border border-top-0 border-bottom-0 border-dark border-end">
                        {item.HSN_code || "N/A"}
                      </td>

                      {/* Quantity */}
                      <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                        {item.sales_quantity
                          ? `${item.sales_quantity} PC`
                          : "N/A"}
                      </td>

                      {/* RATE / UNIT */}
                      <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                        {totalAmount}
                      </td>

                      {/* GST % */}
                      <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                        {item.sales_gst ? `${item.sales_gst}%` : "N/A"}
                      </td>

                      {/* AMOUNT (taxable value) */}
                      <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                        {baseMrp}
                      </td>
                    </tr>
                  );
                })}

                {/* Empty Row for spacing - dynamically adjusted */}
                {emptyRowsNeeded > 0 && (
                  <tr>
                    {[...Array(6)].map((_, i) => (
                      <td
                        key={i}
                        className="border border-top-0 border-bottom-0 border-dark border-end py-1"
                      ></td>
                    ))}
                  </tr>
                )}

                {/* GST Rows - only show on last page */}
                {currentPage === totalPages && (
                  <>
                    {responseData.Sales_Items?.map((item, index) => {
                      if (item.sales_gst) {
                        const halfGst = Number(item.sales_gst) / 2;
                        const gstRate = parseFloat(item.sales_gst || 0) / 100;
                        const mrpIncludingGst = parseFloat(item.Mrp || 0);
                 
                        const itemTaxableValue = item.sales_quantity * mrpIncludingGst;
                        const cgstAmount = (itemTaxableValue * gstRate) / 2;
                        const sgstAmount = cgstAmount;

                        return (
                          <React.Fragment key={index}>
                            <tr>
                              <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                                Output CGST @ {halfGst} %
                              </td>
                              {[...Array(4)].map((_, i) => (
                                <td
                                  key={i}
                                  className="border border-top-0 border-bottom-0 border-dark border-end"
                                ></td>
                              ))}
                              <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                                {cgstAmount.toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                                Output SGST @ {halfGst} %
                              </td>
                              {[...Array(4)].map((_, i) => (
                                <td
                                  key={i}
                                  className="border border-top-0 border-bottom-0 border-dark border-end"
                                ></td>
                              ))}
                              <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                                {sgstAmount.toFixed(2)}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      }
                      return null;
                    })}

                    {/* Round Off */}
                    <tr>
                      <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                        Round Off
                      </td>
                      {[...Array(4)].map((_, i) => (
                        <td
                          key={i}
                          className="border border-top-0 border-bottom-0 border-dark border-end"
                        ></td>
                      ))}
                      <td className="text-end border border-top-0 border-bottom-0 border-dark border-end">
                        {roundOff}
                      </td>
                    </tr>

                    {/* Extra Empty Rows for spacing - dynamically adjusted */}
                    {[
                      ...Array(
                        Math.max(
                          0,
                          10 - (responseData.Sales_Items?.length * 2 || 0)
                        )
                      ),
                    ].map((_, i) => (
                      <tr key={i}>
                        {[...Array(6)].map((_, j) => (
                          <td
                            key={j}
                            className="border border-top-0 border-bottom-0 border-dark border-end py-3"
                          ></td>
                        ))}
                      </tr>
                    ))}

                    {/* SL. NO OF TYRE Row */}
                    <tr>
                      <td className="text-start border border-top-0 border-bottom-0 border-dark border-end">
                        SL. NO OF TYRE:{" "}
                        <span className="fw-semibold">
                          {responseData.Sales_Items?.map(
                            (item) => item.tyre_number
                          ).join(", ")}
                        </span>
                      </td>
                      {[...Array(5)].map((_, i) => (
                        <td
                          key={i}
                          className="border border-top-0 border-bottom-0 border-dark border-end"
                        ></td>
                      ))}
                    </tr>

                    {/* Final Total Row */}
                    <tr>
                      <td colSpan={4} className="border border-dark">
                        <div className="d-flex justify-content-between align-items-center">
                          <span style={{ fontSize: "14px" }}>
                            {numberToWords(Number(roundedTotal))}.
                          </span>
                          <span>TOTAL Rs.</span>
                        </div>
                      </td>
                      <td
                        colSpan={2}
                        className="border border-dark fw-bold text-end"
                        style={{ fontSize: "20px" }}
                      >
                        ₹{" "}
                        {Number(roundedTotal).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>

                    {/* Content */}
                    <tr>
                      <td colSpan={6} className="border border-dark">
                        <h6
                          className="fw-bold text-start pt-1"
                          style={{
                            fontFamily: '"Times New Roman", Times, serif',
                          }}
                        >
                          <u>Warranty Terms</u>
                        </h6>
                        <span
                          className="fw-semibold text-start d-block"
                          style={{
                            fontSize: "14px",
                          }}
                        >
                          All MRF Tyres are covered against manufacturing
                          defects as given below:
                        </span>

                        {/* List */}
                        <div className="row">
                          <div className="col-12">
                            <table className="table table-borderless">
                              <tbody style={{ fontSize: "12px" }}>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • Passenger Car Radial, Motorcycle, Scooter
                                    and Moped tyres
                                  </td>
                                  <td className="text-start">: 6 years</td>
                                </tr>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • Tractor Tyres & Off The Road Tyres
                                  </td>
                                  <td className="text-start">: 6 years</td>
                                </tr>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • Fork lift Solid Tyres
                                  </td>
                                  <td className="text-start">: 5 years</td>
                                </tr>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • Small Commercial Vehicles 3 & 4 wheeler &
                                    Light Commercial Vehicle Tyres (Radial &
                                    Nylon)
                                  </td>
                                  <td className="text-start">: 4 years</td>
                                </tr>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • Truck Bias and Truck Radial Tyres
                                  </td>
                                  <td className="text-start">: 3 years</td>
                                </tr>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • Tubes :- Truck and Light commercial
                                    vehicle
                                  </td>
                                  <td className="text-start">: 2 years</td>
                                </tr>
                                <tr>
                                  <td
                                    className="text-start"
                                    style={{ width: "80%" }}
                                  >
                                    • All other tubes
                                  </td>
                                  <td className="text-start">: 3 years</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <span
                          className="fw-semibold text-start d-block"
                          style={{ marginTop: "-15px" }}
                        >
                          These are subject to Terms & Conditions as per our
                          warranty policy available on MRF's website
                          www.mrftyres.com
                        </span>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {currentPage === totalPages && (
          <div className="row" style={{ width: "102%", marginTop: "-12px" }}>
            <div className="col-12 fs-6">
              <div className="d-flex justify-content-between">
                <span>E.&.O.E</span>
                <span className="fw-bold">for Sri Krishna Tyres</span>
              </div>
            </div>
          </div>
        )}

        {/* Page number indicator */}
        {totalPages > 1 && (
          <div className="text-center mt-2">
            Page {currentPage} of {totalPages}
          </div>
        )}
      </div>

      <style>{`
  @page {
    size: A4;
    margin: 0;
  }

  body {
    background: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th, td {
    padding: 2px 4px !important;
  }

  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: 210mm;
      height: 297mm;
    }

    .a4-wrapper {
      display: flex;
      justify-content: center;
      align-items: start;
      width: 100%;
      margin-left: 24px !important;
      page-break-after: always;
    }

    .container-fluid {
      padding: 10mm !important;
      margin: 0 !important;
    }

    table {
      page-break-inside: avoid;
    }

    .btn, .pagination-controls {
      display: none !important;
    }
  }
`}</style>
    </div>
  );
};

export default Invoice_bill;
